import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { teacherService } from '../../services/classroom/teacherService';
import {
  parseMatchingAnswerMap,
  parseWorkDescription,
  parseWorkUploadAnswer,
} from '../../shared/classroom/workContentParser';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BackLink = styled(Link)`
  width: fit-content;
  color: var(--primary);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1rem 1.1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primaryDark) 100%);
  color: var(--onPrimary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.15rem, 2vw, 1.45rem);
  color: var(--text);
`;

const Meta = styled.div`
  margin-top: 0.2rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.82rem;
`;

const Badge = styled.span<{ $tone: 'pending' | 'graded' | 'missing' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  ${({ $tone }) =>
    $tone === 'graded'
      ? 'background: color-mix(in srgb, var(--success, #1a8f5c) 15%, transparent); color: var(--success, #1a8f5c);'
      : $tone === 'missing'
        ? 'background: color-mix(in srgb, var(--error, #c62828) 14%, transparent); color: var(--error, #c62828);'
        : 'background: color-mix(in srgb, var(--primary) 14%, transparent); color: var(--primary);'}
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 320px);
  gap: 1rem;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  color: var(--text);
`;

const Muted = styled.p`
  margin: 0;
  font-size: 0.87rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const QuestionCard = styled.article`
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  border-radius: 12px;
  padding: 0.8rem 0.85rem;
  margin-bottom: 0.65rem;
`;

const QuestionTop = styled.div`
  margin-bottom: 0.55rem;
`;

const Kicker = styled.div`
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-transform: uppercase;
`;

const Prompt = styled.div`
  margin-top: 0.15rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
`;

const ResponseBlock = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const Label = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-transform: uppercase;
`;

const Value = styled.div`
  font-size: 0.88rem;
  color: var(--text);
  white-space: pre-wrap;
  word-break: break-word;
`;

const MediaAnswer = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const MediaFrame = styled.div`
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: #000;
`;

const MediaImage = styled.img`
  width: 100%;
  max-height: 340px;
  object-fit: cover;
  display: block;
  background: var(--surface);
`;

const MediaVideo = styled.video`
  width: 100%;
  max-height: 340px;
  display: block;
  background: #000;
`;

const MediaIframe = styled.iframe`
  width: 100%;
  min-height: 340px;
  display: block;
  border: 0;
  background: #000;
`;

const MediaMeta = styled.div`
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const AnswerPill = styled.span<{ $tone: 'correct' | 'incorrect' | 'manual' }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  border-radius: 999px;
  padding: 0.18rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  ${({ $tone }) =>
    $tone === 'correct'
      ? 'background: color-mix(in srgb, var(--success, #1a8f5c) 16%, transparent); color: var(--success, #1a8f5c);'
      : $tone === 'incorrect'
        ? 'background: color-mix(in srgb, var(--error, #c62828) 14%, transparent); color: var(--error, #c62828);'
        : 'background: var(--surface); color: var(--textSecondary, var(--onSurfaceVariant)); border: 1px solid var(--outline);'}
`;

const MatchGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.35rem 0.5rem;
`;

const MatchCell = styled.div`
  font-size: 0.84rem;
  color: var(--text);
  padding: 0.4rem 0.45rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surface);
`;

const AutoGradeBox = styled.div`
  margin-bottom: 0.8rem;
  padding: 0.7rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  display: grid;
  gap: 0.45rem;
`;

const AutoGradeTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const AutoGradeText = styled.div`
  font-size: 0.82rem;
  color: var(--text);
`;

const GradeForm = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
`;

const Input = styled.input`
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  padding: 0.5rem 0.65rem;
  font: inherit;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }
`;

const TextArea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  padding: 0.5rem 0.65rem;
  font: inherit;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Btn = styled.button<{ $tone?: 'primary' | 'ghost' }>`
  border-radius: 8px;
  padding: 0.55rem 0.9rem;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  border: ${({ $tone }) => ($tone === 'ghost' ? '1px solid var(--outline)' : 'none')};
  background: ${({ $tone }) => ($tone === 'ghost' ? 'transparent' : 'var(--primary)')};
  color: ${({ $tone }) => ($tone === 'ghost' ? 'var(--text)' : 'var(--onPrimary)')};
  &:hover {
    background: ${({ $tone }) => ($tone === 'ghost' ? 'var(--surfaceVariant)' : 'var(--primaryDark)')};
  }
`;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const first = parts[0][0] ?? '';
  const last = parts[parts.length - 1][0] ?? '';
  return `${first}${last}`.toUpperCase() || '?';
}

function isUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function inferUploadKindFromUrl(value: string): 'image' | 'video' | null {
  const clean = value.trim().toLowerCase();
  if (!clean) return null;
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(clean)) return 'image';
  if (/\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i.test(clean)) return 'video';
  return null;
}

function getEmbeddableVideoUrl(value: string): string | null {
  const clean = value.trim();
  if (!clean) return null;
  if (clean.includes('iframe.mediadelivery.net')) return clean;
  if (clean.includes('youtu.be/')) {
    return clean.replace('youtu.be/', 'www.youtube.com/embed/');
  }
  if (clean.includes('youtube.com/watch?v=')) {
    return clean.replace('watch?v=', 'embed/');
  }
  if (clean.includes('youtube.com/embed/')) return clean;
  return null;
}

function renderStructuredUploadAnswer(answer: string) {
  const upload = parseWorkUploadAnswer(answer);
  const fallbackKind = !upload && isUrl(answer) ? inferUploadKindFromUrl(answer) : null;
  const embeddableUrl = getEmbeddableVideoUrl(upload?.url ?? answer);
  const normalizedUpload = upload
    ? upload
    : fallbackKind
      ? {
          kind: fallbackKind,
          url: answer.trim(),
          status: 'uploaded' as const,
        }
      : null;
  if (!normalizedUpload) return null;

  return (
    <MediaAnswer>
      <MediaFrame>
        {normalizedUpload.kind === 'video' ? (
          embeddableUrl ? (
            <MediaIframe
              src={embeddableUrl}
              title={normalizedUpload.fileName || 'Uploaded video'}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <MediaVideo controls preload="metadata" src={normalizedUpload.url} />
          )
        ) : (
          <MediaImage src={normalizedUpload.url} alt={normalizedUpload.fileName || 'Uploaded response'} />
        )}
      </MediaFrame>
      <MediaMeta>
        {normalizedUpload.fileName || (normalizedUpload.kind === 'video' ? 'Uploaded video' : 'Uploaded image')}
        {normalizedUpload.status === 'processing' ? ' · Still processing' : ''}
      </MediaMeta>
      <Value>
        <a href={normalizedUpload.url} target="_blank" rel="noreferrer">
          Open original file
        </a>
      </Value>
    </MediaAnswer>
  );
}

function statusTone(status: string): 'pending' | 'graded' | 'missing' {
  if (status === 'graded' || status === 'returned') return 'graded';
  if (status === 'missing') return 'missing';
  return 'pending';
}

function statusLabel(status: string): string {
  if (status === 'graded') return 'Graded';
  if (status === 'returned') return 'Returned';
  if (status === 'missing') return 'Missing';
  return 'Ready to Grade';
}

function resolveAnswer(questionId: string, index: number, answers: Record<string, string>): string {
  const exact = answers[questionId];
  if (typeof exact === 'string') return exact;
  const indexKeys = [String(index), String(index + 1), `q${index + 1}`, `question_${index + 1}`];
  for (const key of indexKeys) {
    const v = answers[key];
    if (typeof v === 'string') return v;
  }
  return '';
}

function normalizeComparable(value: string): string {
  return value.trim().toLowerCase();
}

function safeNumber(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function getMatchingStudentLabel(
  question: ReturnType<typeof parseWorkDescription>['questions'][number],
  pairIndex: number,
  answerMap: Record<string, string>
): string {
  const choice = answerMap[String(pairIndex)];
  if (!choice) return '—';
  const numericChoice = Number(choice);
  if (Number.isInteger(numericChoice) && numericChoice >= 0) {
    const rightText = question.pairs?.[numericChoice]?.right?.trim();
    if (rightText) return rightText;
  }
  return choice;
}

type AutoGradeByQuestion = Record<
  string,
  {
    autoGradable: boolean;
    isCorrect: boolean | null;
    earned: number;
    possible: number;
  }
>;

function computeAutoGrade(
  questions: ReturnType<typeof parseWorkDescription>['questions'],
  answersByQuestionId: Record<string, string>
): { earned: number; possible: number; gradedCount: number; byQuestion: AutoGradeByQuestion } {
  let earned = 0;
  let possible = 0;
  let gradedCount = 0;
  const byQuestion: AutoGradeByQuestion = {};

  questions.forEach((question, index) => {
    const studentAnswer = resolveAnswer(question.id, index, answersByQuestionId);
    const points = Math.max(1, safeNumber(question.points));

    if (question.type === 'multiple_choice' || question.type === 'true_false') {
      possible += points;
      gradedCount += 1;
      const isCorrect = normalizeComparable(studentAnswer) === normalizeComparable(question.answer);
      byQuestion[question.id] = {
        autoGradable: true,
        isCorrect,
        earned: isCorrect ? points : 0,
        possible: points,
      };
      if (isCorrect) earned += points;
      return;
    }

    if (question.type === 'matching') {
      const pairs = question.pairs ?? [];
      possible += points;
      gradedCount += 1;
      const matchingMap = parseMatchingAnswerMap(studentAnswer);
      const isCorrect = pairs.length > 0 && pairs.every((_, pairIndex) => matchingMap[String(pairIndex)] === String(pairIndex));
      byQuestion[question.id] = {
        autoGradable: true,
        isCorrect,
        earned: isCorrect ? points : 0,
        possible: points,
      };
      if (isCorrect) earned += points;
      return;
    }

    byQuestion[question.id] = {
      autoGradable: false,
      isCorrect: null,
      earned: 0,
      possible: points,
    };
  });

  return { earned, possible, gradedCount, byQuestion };
}

const ClassroomTeacherSubmissionGradingPage: React.FC = () => {
  const { submissionId = '' } = useParams<{ submissionId: string }>();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'grade-submission', submissionId],
    queryFn: () => teacherService.getGradeSubmissionDetail(submissionId),
    enabled: Boolean(submissionId),
  });

  const detail = detailQuery.data;
  const submission = detail?.submission ?? null;

  const workQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'work', submission?.work_type, submission?.work_id],
    queryFn: () => teacherService.getWorkById(submission!.work_type as 'assignment' | 'quiz', submission!.work_id ?? ''),
    enabled: Boolean(submission?.work_id && (submission?.work_type === 'assignment' || submission?.work_type === 'quiz')),
  });

  const [score, setScore] = useState<number>(0);
  const [feedback, setFeedback] = useState('');
  const [saveError, setSaveError] = useState<string>('');
  const [saveSuccess, setSaveSuccess] = useState<string>('');

  const parsedWork = useMemo(() => parseWorkDescription(workQuery.data?.description), [workQuery.data?.description]);
  const autoGrade = useMemo(
    () => computeAutoGrade(parsedWork.questions, detail?.answers_by_question_id ?? {}),
    [parsedWork.questions, detail?.answers_by_question_id]
  );
  const hasCapturedAnswers = useMemo(
    () => Boolean(detail && Object.keys(detail.answers_by_question_id).length > 0),
    [detail],
  );

  React.useEffect(() => {
    if (!submission) return;
    const shouldUseAutoScore =
      (submission.status === 'submitted' || submission.status === 'missing') &&
      submission.score === null &&
      autoGrade.gradedCount > 0;
    setScore(shouldUseAutoScore ? autoGrade.earned : (submission.score ?? 0));
    setFeedback(submission.feedback ?? '');
    setSaveError('');
    setSaveSuccess('');
  }, [submission, autoGrade.earned, autoGrade.gradedCount]);

  const maxScore = Number(submission?.max_points ?? workQuery.data?.points ?? 100);
  const safeMaxScore = Number.isFinite(maxScore) && maxScore >= 0 ? maxScore : 100;
  const normalizedScore = Number.isFinite(score) ? Math.min(safeMaxScore, Math.max(0, score)) : 0;

  const saveGrade = useMutation({
    mutationFn: async (payload: Parameters<typeof teacherService.saveGrade>[0]) => {
      const result = await teacherService.saveGrade(payload);
      if (!result.ok) {
        throw new Error(result.error ?? 'Unable to save grade.');
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'grades'] });
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'grade-submission', submissionId] });
      setSaveError('');
      setSaveSuccess('Grade saved successfully.');
    },
    onError: (error) => {
      const message = (error as { message?: string })?.message ?? 'Unable to save grade.';
      setSaveSuccess('');
      setSaveError(message);
    },
  });

  if (detailQuery.isLoading) {
    return <Muted>Loading submission…</Muted>;
  }

  if (!submission) {
    return (
      <Page>
        <BackLink to="/classroom-teacher/grades">← Back to grading</BackLink>
        <Card>
          <Muted>Submission not found.</Muted>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <BackLink to="/classroom-teacher/grades">← Back to grading</BackLink>

      <Card>
        <HeaderRow>
          <StudentRow>
            <Avatar aria-hidden>
              {submission.student_avatar_url ? (
                <AvatarImage src={submission.student_avatar_url} alt="" />
              ) : (
                initials(submission.student_name)
              )}
            </Avatar>
            <div>
              <HeaderTitle>{submission.student_name}</HeaderTitle>
              <Meta>
                {submission.work_title}
                {submission.class_name ? ` · ${submission.class_name}` : ''}
                {submission.submitted_at ? ` · Submitted ${new Date(submission.submitted_at).toLocaleString()}` : ''}
              </Meta>
            </div>
          </StudentRow>
          <Badge $tone={statusTone(submission.status)}>{statusLabel(submission.status)}</Badge>
        </HeaderRow>
      </Card>

      <TwoCol>
        <Card>
          <SectionTitle>Student Responses</SectionTitle>
          {!hasCapturedAnswers ? (
            <Muted>
              No per-question answers were found for this submission. This item can still be graded with score and feedback.
            </Muted>
          ) : parsedWork.questions.length === 0 ? (
            <Muted>
              This assignment has no structured question blocks in its description. Raw answer data exists but cannot be mapped to prompts.
            </Muted>
          ) : (
            parsedWork.questions.map((question, index) => {
              const answer = resolveAnswer(question.id, index, detail!.answers_by_question_id);
              const matchingMap = question.type === 'matching' ? parseMatchingAnswerMap(answer) : {};
              const uploadAnswer = question.type === 'file_upload' ? renderStructuredUploadAnswer(answer) : null;
              const questionAuto = autoGrade.byQuestion[question.id];
              const answerTone =
                questionAuto?.autoGradable && questionAuto.isCorrect !== null
                  ? questionAuto.isCorrect
                    ? 'correct'
                    : 'incorrect'
                  : 'manual';

              return (
                <QuestionCard key={question.id}>
                  <QuestionTop>
                    <Kicker>
                      Question {index + 1} · {question.type.replace('_', ' ')} · {question.points} pt
                      {question.points === 1 ? '' : 's'}
                    </Kicker>
                    <Prompt>{question.prompt}</Prompt>
                  </QuestionTop>

                  <ResponseBlock>
                    <Label>Student answer</Label>
                    <AnswerPill $tone={answerTone}>
                      {questionAuto?.autoGradable
                        ? questionAuto.isCorrect
                          ? `Auto-graded: Correct (+${questionAuto.earned})`
                          : `Auto-graded: Incorrect (+${questionAuto.earned})`
                        : 'Manual grading required'}
                    </AnswerPill>
                    {question.type === 'matching' && (question.pairs?.length ?? 0) > 0 ? (
                      <MatchGrid>
                        {(question.pairs ?? []).map((pair, pairIndex) => (
                          <React.Fragment key={`${question.id}-pair-${pairIndex}`}>
                            <MatchCell>{pair.left}</MatchCell>
                            <MatchCell>{getMatchingStudentLabel(question, pairIndex, matchingMap)}</MatchCell>
                          </React.Fragment>
                        ))}
                      </MatchGrid>
                    ) : uploadAnswer ? (
                      uploadAnswer
                    ) : answer.trim() ? (
                      isUrl(answer) ? (
                        <Value>
                          <a href={answer} target="_blank" rel="noreferrer">
                            {answer}
                          </a>
                        </Value>
                      ) : (
                        <Value>{answer}</Value>
                      )
                    ) : (
                      <Muted>— no response —</Muted>
                    )}
                  </ResponseBlock>

                  <ResponseBlock style={{ marginTop: '0.55rem' }}>
                    <Label>Correct answer</Label>
                    {question.type === 'matching' && (question.pairs?.length ?? 0) > 0 ? (
                      <MatchGrid>
                        {(question.pairs ?? []).map((pair, pairIndex) => (
                          <React.Fragment key={`${question.id}-correct-${pairIndex}`}>
                            <MatchCell>{pair.left}</MatchCell>
                            <MatchCell>{pair.right || '—'}</MatchCell>
                          </React.Fragment>
                        ))}
                      </MatchGrid>
                    ) : question.answer.trim() ? (
                      <Value>{question.answer}</Value>
                    ) : (
                      <Muted>— no answer key set —</Muted>
                    )}
                  </ResponseBlock>
                </QuestionCard>
              );
            })
          )}
        </Card>

        <Card>
          <SectionTitle>Grade & Feedback</SectionTitle>
          {autoGrade.gradedCount > 0 ? (
            <AutoGradeBox>
              <AutoGradeTop>
                <AutoGradeText>
                  Auto-grade suggestion: <strong>{autoGrade.earned}</strong> / {autoGrade.possible} from {autoGrade.gradedCount}{' '}
                  objective question{autoGrade.gradedCount === 1 ? '' : 's'}.
                </AutoGradeText>
                <Btn type="button" $tone="ghost" onClick={() => setScore(Math.min(safeMaxScore, Math.max(0, autoGrade.earned)))}>
                  Use Auto Score
                </Btn>
              </AutoGradeTop>
              <Muted>
                Multiple choice, true/false, and matching are auto-graded. You can edit the score before saving.
              </Muted>
            </AutoGradeBox>
          ) : null}
          <GradeForm>
            <Field>
              Score (max {safeMaxScore})
              <Input
                type="number"
                min={0}
                max={safeMaxScore}
                value={Number.isNaN(score) ? 0 : score}
                onChange={(e) => setScore(Number(e.target.value))}
              />
            </Field>

            <Field>
              Feedback
              <TextArea rows={6} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
            </Field>
            {saveError ? <Muted style={{ color: 'var(--error, #c62828)' }}>{saveError}</Muted> : null}
            {saveSuccess ? <Muted style={{ color: 'var(--success, #1a8f5c)' }}>{saveSuccess}</Muted> : null}

            <ActionRow>
              <Btn
                type="button"
                onClick={() =>
                  saveGrade.mutate({
                    id: submission.id,
                    score: normalizedScore,
                    feedback,
                    status: 'graded',
                  })
                }
                disabled={saveGrade.isPending}
              >
                Save Grade
              </Btn>
              <Btn
                type="button"
                $tone="ghost"
                onClick={() =>
                  saveGrade.mutate({
                    id: submission.id,
                    score: normalizedScore,
                    feedback,
                    status: 'returned',
                  })
                }
                disabled={saveGrade.isPending}
              >
                Mark Returned
              </Btn>
            </ActionRow>
          </GradeForm>
        </Card>
      </TwoCol>
    </Page>
  );
};

export default ClassroomTeacherSubmissionGradingPage;
